import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { stopImpersonation as apiStopImpersonation, getMessagesUnreadCount } from "../lib/api";
import { toast } from "sonner";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCircle,
  FolderKanban,
  ClipboardList,
  ShoppingCart,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  FileText,
  Settings,
  DollarSign,
  FileSearch,
  Repeat,
  Archive,
  Upload,
  Eye,
  KeyRound,
  MessageSquare,
  Send,
  BookOpen,
  Wand2,
  Sparkles,
  LayoutGrid,
  ScanEye,
  Gauge,
  Calculator,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_partner-hub-115/artifacts/i4omx69t_rp%20LOGO.png";

export function Layout({ children }) {
  const { user, logout, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [replyNeededCount, setReplyNeededCount] = useState(0);

  // Poll reply-needed message count every 60s (and on route change)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await getMessagesUnreadCount();
        if (!cancelled) setReplyNeededCount(res.data?.reply_needed_count || 0);
      } catch {
        /* silent */
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isImpersonating = !!user?.impersonator_id;
  const handleExitImpersonation = async () => {
    setExitingImpersonation(true);
    try {
      await apiStopImpersonation();
      await checkAuth();
      toast.success("Back to your Super Admin account");
      navigate("/users");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to exit impersonation");
    } finally {
      setExitingImpersonation(false);
    }
  };

  const navItems = [
    { label: "Dashboard", icon: LayoutDashboard, href: "/dashboard", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Unified Inbox", icon: MessageSquare, href: "/messages", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Direct Messages", icon: Send, href: "/direct", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Agencies", icon: Building2, href: "/agencies", roles: ["super_admin"] },
    { label: "Users", icon: Users, href: "/users", roles: ["super_admin", "manager", "ops_coordinator"] },
    { label: "Book New Order", icon: ShoppingCart, href: "/orders/new", roles: ["manager", "rep"] },
    // Prospect IQ — everything a rep uses to research and pitch a prospect
    // lives under this collapsible group so the sidebar stays tight.
    {
      label: "Prospect IQ",
      icon: Sparkles,
      href: "/prospect-iq",
      roles: ["super_admin", "manager", "rep", "ops_coordinator"],
      group: true,
      children: [
        { label: "Hub", icon: Sparkles, href: "/prospect-iq" },
        { label: "Proposals & Audits", icon: FileSearch, href: "/proposals" },
        { label: "Prospect IQ Proposal", icon: Sparkles, href: "/prospect-proposals",
          roles: ["super_admin", "manager", "rep"] },
        { label: "Prospect Audit", icon: Gauge, href: "/prospect-iq/audits",
          roles: ["super_admin", "manager", "rep"] },
        { label: "Competitor Reports", icon: ScanEye, href: "/prospect-iq/competitor-reports",
          roles: ["super_admin", "manager", "rep"] },
        { label: "Homepage Mockups", icon: Wand2, href: "/mockups",
          roles: ["super_admin", "manager", "rep"] },
        { label: "ROI Estimator", icon: Calculator, href: "/resources/roi-estimator" },
        { label: "Slide Library", icon: LayoutGrid, href: "/resources/slide-library" },
      ],
    },
    { label: "New Client Onboarding", icon: FolderKanban, href: "/orders/active", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Monthly Active Clients", icon: Repeat, href: "/clients/monthly-active", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Archived Clients", icon: Archive, href: "/orders/archived", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "All Clients (Admin)", icon: UserCircle, href: "/clients", roles: ["super_admin", "manager", "ops_coordinator"] },
    { label: "Intake Forms", icon: FileText, href: "/intake-forms", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Resources", icon: BookOpen, href: "/resources", roles: ["super_admin", "manager", "rep", "ops_coordinator"] },
    { label: "Pending Billing", icon: DollarSign, href: "/billing", roles: ["super_admin", "manager"] },
    { label: "Bulk Import Clients", icon: Upload, href: "/admin/bulk-import", roles: ["super_admin"] },
    { label: "Slide Library (Admin)", icon: LayoutGrid, href: "/admin/slide-library", roles: ["super_admin"] },
    { label: "Settings", icon: Settings, href: "/settings", roles: ["super_admin", "manager"] },
  ];

  // Prospect IQ group is expanded by default when the user is anywhere inside
  // one of its pages, otherwise closed. Manual toggle overrides.
  const inProspectIq = (
    location.pathname.startsWith("/prospect-iq") ||
    location.pathname.startsWith("/prospect-proposals") ||
    location.pathname.startsWith("/mockups") ||
    location.pathname.startsWith("/proposals") ||
    location.pathname.startsWith("/resources/roi-estimator") ||
    location.pathname.startsWith("/resources/slide-library")
  );
  const [expandedGroup, setExpandedGroup] = useState(inProspectIq ? "Prospect IQ" : null);
  useEffect(() => {
    if (inProspectIq && expandedGroup !== "Prospect IQ") setExpandedGroup("Prospect IQ");
    // Do NOT auto-collapse when the user navigates away — respects manual open.
  }, [inProspectIq]);

  const filteredNavItems = navItems.filter((item) => item.roles.includes(user?.role));

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: "Super Admin",
      manager: "Manager",
      rep: "Sales Rep",
      ops_coordinator: "Ops Coordinator",
    };
    return badges[role] || role;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 border-b bg-card z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <img src={LOGO_URL} alt="Ready Prospect" className="h-8 object-contain" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-user-menu">
              <Bell className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-card border-r transform transition-transform duration-200 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-32 flex items-center justify-center border-b px-4">
            <img src={LOGO_URL} alt="Ready Prospect" className="object-contain" style={{ height: "100px" }} />
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {filteredNavItems.map((item) => {
                // Grouped item — collapsible parent with children
                if (item.group) {
                  const isExpanded = expandedGroup === item.label;
                  const anyChildActive = (item.children || []).some((c) =>
                    location.pathname === c.href || location.pathname.startsWith(c.href + "/"));
                  return (
                    <div key={item.label}>
                      <button
                        type="button"
                        onClick={() => setExpandedGroup(isExpanded ? null : item.label)}
                        className={cn("sidebar-link w-full", anyChildActive && !isExpanded && "active")}
                        data-testid={`nav-group-${item.label.toLowerCase().replace(/ /g, "-")}`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronRight className={cn(
                          "h-4 w-4 transition-transform text-muted-foreground",
                          isExpanded && "rotate-90",
                        )} />
                      </button>
                      {isExpanded && (
                        <div className="ml-2 mt-1 space-y-1 border-l pl-2">
                          {(item.children || [])
                            .filter((c) => !c.roles || c.roles.includes(user?.role))
                            .map((child) => {
                              const childActive = location.pathname === child.href ||
                                                  (child.href !== item.href && location.pathname.startsWith(child.href + "/"));
                              const ChildIcon = child.icon;
                              return (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className={cn("sidebar-link text-sm", childActive && "active")}
                                  onClick={() => setSidebarOpen(false)}
                                  data-testid={`nav-child-${child.label.toLowerCase().replace(/ /g, "-")}`}
                                >
                                  <ChildIcon className="h-4 w-4" />
                                  <span className="flex-1">{child.label}</span>
                                </Link>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                }
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                const showBadge = item.href === "/messages" && replyNeededCount > 0;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn("sidebar-link", isActive && "active")}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="flex-1">{item.label}</span>
                    {showBadge && (
                      <span
                        className="inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[11px] font-semibold px-1.5"
                        data-testid="nav-messages-unread-badge"
                      >
                        {replyNeededCount > 99 ? "99+" : replyNeededCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User info */}
          <div className="border-t p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 h-auto py-3"
                  data-testid="user-menu-trigger"
                >
                  <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{getRoleBadge(user?.role)}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.email}</p>
                  {user?.agency_name && (
                    <p className="text-xs text-muted-foreground">{user.agency_name}</p>
                  )}
                </div>
                <DropdownMenuSeparator />
                {!isImpersonating && (
                  <DropdownMenuItem
                    onClick={() => setChangePasswordOpen(true)}
                    data-testid="change-password-menu-item"
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Change Password
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        {isImpersonating && (
          <div
            className="sticky top-0 z-30 bg-amber-500 text-amber-950 border-b border-amber-600 shadow-sm"
            data-testid="impersonation-banner"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Eye className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  <strong>Viewing as {user?.name}</strong>
                  {user?.role && <span className="ml-1 opacity-80">({user.role === "rep" ? "Sales Rep" : user.role === "manager" ? "Manager" : user.role}{user?.agency_name ? ` @ ${user.agency_name}` : ""})</span>}
                  <span className="ml-2 opacity-80">— Read-only. Mutations are blocked.</span>
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="bg-white/90 hover:bg-white text-amber-950 border-amber-700"
                onClick={handleExitImpersonation}
                disabled={exitingImpersonation}
                data-testid="exit-impersonation-btn"
              >
                {exitingImpersonation ? "Exiting…" : "Exit Impersonation"}
              </Button>
            </div>
          </div>
        )}
        <div className="p-6 lg:p-8">{children}</div>
      </main>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
